/**
 * 封禁管理组件
 *
 * 管理封禁用户列表、封禁操作、解封操作。
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  safeguardAdminApi,
  type BannedUser,
  type BanRecord,
} from '../../services/safeguardAdminApi';

const BAN_DURATIONS = [
  { label: '1小时', hours: 1 },
  { label: '24小时', hours: 24 },
  { label: '7天', hours: 168 },
  { label: '30天', hours: 720 },
  { label: '永久', hours: 0 },
];

export const BanManagement: React.FC = () => {
  const [bannedUsers, setBannedUsers] = useState<BannedUser[]>([]);
  const [banRecords, setBanRecords] = useState<BanRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [banModal, setBanModal] = useState<{ userId: number; email: string } | null>(null);
  const [banDuration, setBanDuration] = useState(24);
  const [banReason, setBanReason] = useState('');

  const [unbanModal, setUnbanModal] = useState<{ userId: number; email: string } | null>(null);
  const [unbanReason, setUnbanReason] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [users, records] = await Promise.all([
        safeguardAdminApi.getBannedUsers(),
        safeguardAdminApi.getBanRecords({ limit: 50 }),
      ]);
      setBannedUsers(users);
      setBanRecords(records);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBan = async () => {
    if (!banModal) return;
    try {
      await safeguardAdminApi.banUser(banModal.userId, banDuration, banReason || '管理员手动封禁');
      setBanModal(null);
      setBanReason('');
      setBanDuration(24);
      setLoading(true);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : '封禁失败');
    }
  };

  const handleUnban = async () => {
    if (!unbanModal) return;
    try {
      await safeguardAdminApi.unbanUser(unbanModal.userId, unbanReason || '管理员手动解封');
      setUnbanModal(null);
      setUnbanReason('');
      setLoading(true);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : '解封失败');
    }
  };

  const formatRemaining = (hours: number | null) => {
    if (hours === null) return '永久';
    if (hours < 1) return '即将到期';
    if (hours < 24) return `${Math.round(hours)}小时`;
    return `${Math.round(hours / 24)}天`;
  };

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
      )}

      {/* 快速封禁入口 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3">快速封禁</h3>
        <QuickBanForm
          onBan={(userId, email) => {
            setBanModal({ userId, email });
          }}
        />
      </div>

      {/* 当前封禁用户 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-700">当前封禁用户 ({bannedUsers.length})</h3>
          <button
            onClick={() => { setLoading(true); fetchData(); }}
            className="text-xs text-blue-500 hover:text-blue-700"
          >
            刷新
          </button>
        </div>

        {bannedUsers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">用户</th>
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">封禁时间</th>
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">到期时间</th>
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">剩余</th>
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">原因</th>
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">操作</th>
                </tr>
              </thead>
              <tbody>
                {bannedUsers.map(u => (
                  <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2 px-3">
                      <div>{u.nickname || u.email}</div>
                      <div className="text-xs text-gray-400">{u.email}</div>
                    </td>
                    <td className="py-2 px-3 text-xs text-gray-500">
                      {u.banned_at ? new Date(u.banned_at).toLocaleString('zh-CN') : '-'}
                    </td>
                    <td className="py-2 px-3 text-xs text-gray-500">
                      {u.is_permanent ? '永久' : (u.banned_until ? new Date(u.banned_until).toLocaleString('zh-CN') : '-')}
                    </td>
                    <td className="py-2 px-3 text-xs">
                      <span className={u.remaining_hours && u.remaining_hours < 1 ? 'text-yellow-600' : 'text-red-600'}>
                        {formatRemaining(u.remaining_hours)}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-xs text-gray-600 max-w-[200px] truncate">{u.banned_reason || '-'}</td>
                    <td className="py-2 px-3">
                      <button
                        onClick={() => setUnbanModal({ userId: u.id, email: u.email })}
                        className="text-green-600 hover:text-green-700 text-xs font-medium"
                      >
                        解封
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center text-gray-400 py-4 text-sm">暂无封禁用户</div>
        )}
      </div>

      {/* 封禁操作记录 */}
      {banRecords.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">操作记录</h3>
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {banRecords.map(r => (
              <div key={r.id} className="flex items-center gap-2 text-xs py-1.5 border-b border-gray-50 last:border-0">
                <span className="text-gray-400 whitespace-nowrap">
                  {r.created_at ? new Date(r.created_at).toLocaleString('zh-CN') : '-'}
                </span>
                <span className={`px-1.5 py-0.5 rounded ${
                  r.action_type === 'ban' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                }`}>
                  {r.action_type === 'ban' ? '封禁' : '解封'}
                </span>
                <span className={`px-1 py-0.5 rounded ${
                  r.ban_type === 'auto' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                }`}>
                  {r.ban_type === 'auto' ? '自动' : '手动'}
                </span>
                <span className="text-gray-600">
                  {r.user_nickname || r.user_email || `用户${r.user_id}`}
                </span>
                {r.duration_hours > 0 && (
                  <span className="text-gray-400">{r.duration_hours}小时</span>
                )}
                <span className="text-gray-400 flex-1 truncate">{r.reason || '-'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 封禁弹窗 */}
      {banModal && (
        <ModalOverlay onClose={() => setBanModal(null)}>
          <h3 className="font-medium mb-4">封禁用户</h3>
          <div className="text-sm text-gray-600 mb-4">
            用户: <span className="font-medium">{banModal.email}</span>
          </div>
          <div className="mb-3">
            <label className="block text-sm text-gray-500 mb-1">封禁时长</label>
            <div className="flex flex-wrap gap-2">
              {BAN_DURATIONS.map(d => (
                <button
                  key={d.hours}
                  onClick={() => setBanDuration(d.hours)}
                  className={`px-3 py-1.5 rounded-lg text-sm border ${
                    banDuration === d.hours
                      ? 'border-red-300 bg-red-50 text-red-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm text-gray-500 mb-1">封禁原因</label>
            <textarea
              value={banReason}
              onChange={e => setBanReason(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none"
              rows={3}
              placeholder="请输入封禁原因..."
            />
          </div>
          <div className="flex gap-3 justify-end">
            <button onClick={() => setBanModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
              取消
            </button>
            <button onClick={handleBan} className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600">
              确认封禁
            </button>
          </div>
        </ModalOverlay>
      )}

      {/* 解封弹窗 */}
      {unbanModal && (
        <ModalOverlay onClose={() => setUnbanModal(null)}>
          <h3 className="font-medium mb-4">解封用户</h3>
          <div className="text-sm text-gray-600 mb-4">
            用户: <span className="font-medium">{unbanModal.email}</span>
          </div>
          <div className="mb-4">
            <label className="block text-sm text-gray-500 mb-1">解封原因</label>
            <textarea
              value={unbanReason}
              onChange={e => setUnbanReason(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none"
              rows={3}
              placeholder="请输入解封原因..."
            />
          </div>
          <div className="flex gap-3 justify-end">
            <button onClick={() => setUnbanModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
              取消
            </button>
            <button onClick={handleUnban} className="px-4 py-2 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600">
              确认解封
            </button>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
};

const QuickBanForm: React.FC<{ onBan: (userId: number, email: string) => void }> = ({ onBan }) => {
  const [input, setInput] = useState('');
  const [error, setError] = useState('');

  const handleQuickBan = () => {
    const id = parseInt(input, 10);
    if (isNaN(id) || id <= 0) {
      setError('请输入有效的用户ID');
      return;
    }
    setError('');
    onBan(id, `用户${id}`);
  };

  return (
    <div className="flex gap-3 items-center">
      <input
        type="number"
        value={input}
        onChange={e => { setInput(e.target.value); setError(''); }}
        placeholder="输入用户ID"
        className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-48"
        onKeyDown={e => e.key === 'Enter' && handleQuickBan()}
      />
      <button
        onClick={handleQuickBan}
        className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600"
      >
        封禁
      </button>
      {error && <span className="text-xs text-red-500">{error}</span>}
    </div>
  );
};

const ModalOverlay: React.FC<{ onClose: () => void; children: React.ReactNode }> = ({ onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-5" onClick={e => e.stopPropagation()}>
      {children}
    </div>
  </div>
);
